// Shared "replace assignees" bulk action (#878).
//
// Extracted out of BulkActionToolbar so the assignee bulk-patch UI — trigger,
// AssigneePicker wiring, and the once-on-close staging semantics — exists in
// exactly ONE place. This codebase already has bugs (#877, #879) that came
// from the same concept existing twice; every host that needs a bulk
// "replace assignees" action (BulkActionToolbar, TableView, MyTasks) renders
// this component instead of holding its own copy.
//
// `PATCH /api/tasks/batch`'s `assignee_ids` REPLACES a task's assignee set —
// it is not an add/remove delta. Offering "add"/"remove" would require
// computing a per-task union/difference client-side, which cannot be
// expressed as the single batch request this component is built around (that
// would mean one request per task, defeating the point of a bulk action and
// multiplying notification/audit writes). We therefore deliberately implement
// REPLACE-only semantics and make that unambiguous in the UI (label + picker
// copy: "Zuständige ersetzen").

import { useEffect, useState } from "preact/hooks";
import { Users } from "lucide-preact";
import { AssigneePicker } from "./AssigneePicker";

export interface BulkAssigneeActionProps {
  /** Replace the assignee set on every selected task with `ids` (one PATCH
   *  /api/tasks/batch request for the whole selection). */
  onSetAssignees: (ids: number[]) => void;
  /** Distinct project ids across the current (assignee-eligible) selection.
   *  See `resolveBulkAssigneeProjectId`. */
  projectIds: number[];
  /** Changing this value resets the picker's local staging state — pass the
   *  selection size (or another value that changes whenever the selection
   *  does) so a fresh bulk action never inherits a stale in-progress pick. */
  resetKey: number | string;
  busy?: boolean;
  compact?: boolean;
  /** Class for the outer wrapper (icon label + picker). */
  className?: string;
  /** Class for the "Zuständige ersetzen" label — each host styles this to
   *  match its own bulk bar (dark floating chip vs. bordered toolbar button). */
  labelClassName?: string;
}

/**
 * Resolve the projectId to scope the bulk-assignee picker to: the common
 * project id when the whole (eligible) selection belongs to exactly one
 * project, otherwise `undefined` so the picker falls back to "all users"
 * (#878 — a multi-project selection has no single member list valid for
 * every task in it; the server accepts any active user as an assignee
 * regardless of project membership, so "all users" is always safe).
 */
export function resolveBulkAssigneeProjectId(projectIds: number[]): number | undefined {
  const distinct = new Set(projectIds);
  return distinct.size === 1 ? [...distinct][0] : undefined;
}

/**
 * Split a bulk selection into records the assignee action can act on
 * (`source === "project"`) and a count of records it must skip. Personal
 * tasks have no assignees at all, so they can never be part of an
 * `assignee_ids` batch request — callers must surface `skippedCount` to the
 * user rather than silently dropping those tasks from the request or letting
 * them silently fail server-side (#878, MyTasks mixes both sources).
 */
export function splitBulkAssigneeEligible<T extends { source: "project" | "personal" }>(
  records: T[]
): { eligible: T[]; skippedCount: number } {
  const eligible = records.filter((r) => r.source === "project");
  return { eligible, skippedCount: records.length - eligible.length };
}

/**
 * Decide whether a closed picker menu should commit its staged picks: only
 * when the user actually touched the picker (toggled at least one checkbox).
 * `null` means "do not call `onSetAssignees`" — an accidental open+dismiss
 * (no toggles) must never wipe every selected task's assignees down to
 * `pending`'s initial `[]`.
 */
export function resolveAssigneeCommit(touched: boolean, pending: number[]): number[] | null {
  return touched ? pending : null;
}

export function BulkAssigneeAction(props: BulkAssigneeActionProps): preact.JSX.Element {
  const { busy = false, compact = true } = props;

  // Staging area: the picker's own onChange fires on every checkbox toggle,
  // but we only want ONE PATCH /api/tasks/batch request per bulk action. So
  // we accumulate picks locally and only call `onSetAssignees` once, when the
  // picker menu closes — and only if the user actually touched it, so an
  // accidental open+dismiss can never wipe every selected task's assignees.
  const [pending, setPending] = useState<number[]>([]);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setPending([]);
    setTouched(false);
  }, [props.resetKey]);

  const assigneeProjectId = resolveBulkAssigneeProjectId(props.projectIds);

  // AssigneePicker has no `disabled` prop of its own, so a busy bulk action
  // is blocked here by disabling pointer interaction on the whole control.
  return (
    <span
      class={`inline-flex items-center gap-1 ${busy ? "pointer-events-none opacity-50" : ""} ${props.className ?? ""}`}
    >
      <span class={props.labelClassName} aria-hidden="true">
        <Users size={14} /> Zuständige ersetzen
      </span>
      <AssigneePicker
        value={pending}
        onChange={(next) => {
          setPending(next);
          setTouched(true);
        }}
        onClose={() => {
          const commit = resolveAssigneeCommit(touched, pending);
          if (commit !== null) props.onSetAssignees(commit);
        }}
        compact={compact}
        label="Zuständige ersetzen (Massenbearbeitung)"
        projectId={assigneeProjectId}
      />
    </span>
  );
}
